import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, CircularProgress, Button } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getFirstNavigationPath } from '../config/navigationConfig';

const AuthSuccess = () => {
  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState('');
  const [error, setError] = useState(null);
  const [waitingForAuth, setWaitingForAuth] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  useEffect(() => {
    const handleAuthSuccess = async () => {
      try {
        // Check for token in URL
        const urlParams = new URLSearchParams(location.search);
        const token = urlParams.get('token');
        
        console.log('🔄 AuthSuccess: Processing auth callback', {
          hasToken: !!token,
          environment: process.env.NODE_ENV,
          url: window.location.href
        });
        
        if (token) {
          // Store token
          localStorage.setItem('auth_token', token);
          console.log('✅ AuthSuccess: Token stored in localStorage');
          
          // Clean up URL
          window.history.replaceState({}, document.title, window.location.pathname);
          
          // Set user name for display
          setUserName('Ihr Google-Konto');
        } else {
          console.log('⚠️ AuthSuccess: No token found in URL');
        }
        
        // Delay for better UX - longer in production for auth verification
        const delayMs = process.env.NODE_ENV === 'production' ? 3000 : 1500;
        await new Promise(resolve => setTimeout(resolve, delayMs));
        setLoading(false);
        
      } catch (err) {
        console.error('Error handling auth success:', err);
        setError('Fehler bei der Authentifizierung: ' + err.message);
        setLoading(false);
      }
    };

    handleAuthSuccess();
  }, [location]);

  // Separate useEffect for coordinated redirect with AuthContext
  useEffect(() => {
    if (!loading && !error) {
      setWaitingForAuth(true);
      console.log('🔄 AuthSuccess: Waiting for AuthContext to complete authentication...');
      
      // Wait for AuthContext to complete authentication
      const checkAuthCompletion = () => {
        console.log('🔍 AuthSuccess: Checking auth state', {
          authLoading,
          hasUser: !!user,
          environment: process.env.NODE_ENV
        });
        
        if (!authLoading) {
          if (user) {
            console.log('✅ AuthSuccess: AuthContext has user, redirecting to first navigation page');
            const firstPage = getFirstNavigationPath(null, user.isAdmin?.());
            navigate(firstPage);
          } else {
            console.log('❌ AuthSuccess: AuthContext has no user, retrying in 1s...');
            // If AuthContext still doesn't have user after loading, retry
            setTimeout(checkAuthCompletion, 1000);
          }
        } else {
          // Still loading, check again in 500ms
          setTimeout(checkAuthCompletion, 500);
        }
      };
      
      // Start checking after token is stored
      setTimeout(checkAuthCompletion, 1000);
    }
  }, [loading, error, authLoading, user, navigate]);

  const handleContinue = () => {
    const firstPage = getFirstNavigationPath(null, user?.isAdmin?.());
    navigate(firstPage);
  };

  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '100vh',
          bgcolor: '#f8fafc'
        }}
      >
        <CircularProgress 
          size={60} 
          thickness={4} 
          sx={{ 
            color: '#6366f1',
            mb: 3
          }} 
        />
        <Typography variant="h6" sx={{ color: '#64748b' }}>
          Verbindung wird hergestellt...
        </Typography>
      </Box>
    );
  }

  if (waitingForAuth) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '100vh',
          bgcolor: '#f8fafc'
        }}
      >
        <CircularProgress 
          size={60} 
          thickness={4} 
          sx={{ 
            color: '#10b981',
            mb: 3
          }} 
        />
        <Typography variant="h6" sx={{ color: '#64748b', mb: 2 }}>
          Anmeldung wird abgeschlossen...
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748b', textAlign: 'center', maxWidth: 400 }}>
          Warten auf Authentifizierung. Falls das länger dauert, öffnen Sie die Browser-Konsole für Details.
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '100vh',
          bgcolor: '#f8fafc',
          p: 3
        }}
      >
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            borderRadius: 3, 
            textAlign: 'center',
            maxWidth: 480
          }}
        >
          <Typography 
            variant="h5" 
            component="h1" 
            sx={{ 
              color: '#ef4444', 
              mb: 3, 
              fontWeight: 700 
            }}
          >
            Fehler bei der Authentifizierung
          </Typography>
          <Typography paragraph sx={{ mb: 3, color: '#64748b' }}>
            {error}
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleContinue}
            sx={{ 
              borderRadius: 2,
              py: 1,
              px: 3,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Zurück zur Startseite
          </Button>
        </Paper>
      </Box>
    );
  }


  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        bgcolor: '#f8fafc',
        p: 3
      }}
    >
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          borderRadius: 3, 
          textAlign: 'center',
          maxWidth: 480,
          position: 'relative'
        }}
      >
        
        <CheckCircleIcon 
          sx={{ 
            fontSize: 70, 
            color: '#10b981', 
            mb: 3 
          }} 
        />
        <Typography 
          variant="h5" 
          component="h1" 
          sx={{ 
            color: '#1e293b', 
            mb: 1, 
            fontWeight: 700 
          }}
        >
          Verbindung hergestellt!
        </Typography>
        <Typography 
          variant="h6" 
          sx={{ 
            color: '#10b981', 
            mb: 3, 
            fontWeight: 600 
          }}
        >
          {userName}
        </Typography>
        <Typography paragraph sx={{ mb: 3, color: '#64748b' }}>
          Ihre Anmeldung war erfolgreich! Sie werden automatisch zu Ihrem Dashboard weitergeleitet, wo Sie Ihre Google Business Profile verwalten und optimieren können.
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleContinue}
          sx={{ 
            borderRadius: 2,
            py: 1,
            px: 3,
            textTransform: 'none',
            fontWeight: 600
          }}
        >
          Weiter zum Dashboard
        </Button>
      </Paper>
    </Box>
  );
};

export default AuthSuccess;